import type { AudioHandle, AudioResult } from '@financialharmony/show-and-tell';

const PREFERRED_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'] as const;
const AUDIO_BITS_PER_SECOND = 32_000;

interface MicrophoneDeps {
  mediaDevices: Pick<MediaDevices, 'getUserMedia'>;
  MediaRecorderCtor: typeof MediaRecorder;
}

function defaultDeps(): MicrophoneDeps {
  return { mediaDevices: navigator.mediaDevices, MediaRecorderCtor: MediaRecorder };
}

/** Wraps getUserMedia + MediaRecorder as the package's AudioHandle. Audio only: no screen (D12). */
export function createMicrophoneSource(deps?: MicrophoneDeps): () => Promise<AudioHandle> {
  return async () => {
    const { mediaDevices, MediaRecorderCtor } = deps ?? defaultDeps();
    // A rejection here (NotAllowedError, NotFoundError) is classified by the recorder, not here.
    const stream = await mediaDevices.getUserMedia({ audio: true });
    const mimeType = PREFERRED_TYPES.find((type) => MediaRecorderCtor.isTypeSupported(type));
    const recorder = new MediaRecorderCtor(stream, { mimeType, audioBitsPerSecond: AUDIO_BITS_PER_SECOND });
    const chunks: Blob[] = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    const stopTracks = () => stream.getTracks().forEach((track) => track.stop());

    return {
      start: () => recorder.start(),
      pause: () => recorder.pause(),
      resume: () => recorder.resume(),
      stop: () =>
        new Promise<AudioResult>((resolve) => {
          recorder.onstop = () => {
            stopTracks();
            const blob = new Blob(chunks, { type: recorder.mimeType });
            resolve({ mimeType: recorder.mimeType, bytes: blob.size, data: blob });
          };
          if (recorder.state === 'inactive') {
            recorder.onstop?.(new Event('stop'));
          } else {
            recorder.stop();
          }
        }),
      cancel: () => {
        recorder.ondataavailable = null;
        recorder.onstop = null;
        if (recorder.state !== 'inactive') recorder.stop();
        stopTracks();
      },
      onEnded: (cb) => {
        stream.getAudioTracks().forEach((track) => track.addEventListener('ended', cb));
      },
    };
  };
}
